/**
 * command-palette.tsx — Bảng lệnh ⌘K / Ctrl+K: gõ tên trang, mục cài đặt hoặc thao tác để mở nhanh.
 *
 * Mở bằng phím tắt ở mọi màn (kể cả đang gõ trong ô nhập) hoặc kính lúp đầu menu trái (side-nav.tsx).
 * Lệnh lấy từ command-items.ts (đã lọc theo vai trò như menu), tìm không dấu bằng lib/command-search.ts.
 * Chỉ gắn ở bố cục máy tính (app-layout.tsx) — điện thoại không có phím tắt.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Search } from 'lucide-react'
import { DialogOverlay, DialogPortal } from '@/components/ui/dialog'
import { useUiStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'
import { useMyPermissions } from '@/hooks/use-settings'
import { MOD_KEY_LABEL, searchCommands } from '@/lib/command-search'
import { cn } from '@/lib/utils'
import { buildCommandItems, commandSectionLabel, OPEN_FEEDBACK_EVENT, type CommandItem } from './command-items'

const LIST_ID = 'command-palette-list'

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border bg-muted px-1 font-sans text-[11px] text-muted-foreground">
      {children}
    </kbd>
  )
}

export function CommandPalette() {
  const { t } = useTranslation('sharedMisc')
  const open = useUiStore((s) => s.commandOpen)
  const setOpen = useUiStore((s) => s.setCommandOpen)
  const role = useAuthStore((s) => s.user?.role)
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const { data: perms } = useMyPermissions()
  const items = useMemo(() => buildCommandItems(role, perms), [role, perms])
  const results = useMemo(() => searchCommands(items, query), [items, query])
  // Chưa gõ gì → chia theo nhóm (Đi tới / Cài đặt / Thao tác nhanh); đang gõ → một danh sách xếp theo độ khớp.
  const grouped = !query.trim()

  // ⌘K / Ctrl+K ở mọi nơi: bật / tắt bảng. Bỏ qua khi có Shift / Alt để không nuốt tổ hợp khác.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'k') return
      e.preventDefault()
      const { commandOpen, setCommandOpen } = useUiStore.getState()
      setCommandOpen(!commandOpen)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Mỗi lần mở là một lần tìm mới.
  useEffect(() => {
    if (open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active, results])

  const run = (item: CommandItem) => {
    setOpen(false)
    if (item.run === 'feedback') window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT))
    else if (item.to) navigate(item.to)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Đang gõ dấu (bộ gõ tiếng Việt / IME) — phím Enter, mũi tên thuộc về bộ gõ.
    if (e.nativeEvent.isComposing) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (results.length ? (i + 1) % results.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0))
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault()
      run(results[active])
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-black/30 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[12vh] z-50 flex max-h-[70vh] w-[min(600px,92vw)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border bg-background shadow-2xl animate-fade-in"
        >
          <DialogPrimitive.Title className="sr-only">{t('commandPalette.title')}</DialogPrimitive.Title>
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActive(0)
              }}
              onKeyDown={onKeyDown}
              placeholder={t('commandPalette.placeholder')}
              role="combobox"
              aria-expanded
              aria-controls={LIST_ID}
              aria-activedescendant={results[active] ? `cmd-${results[active].id}` : undefined}
              className="h-12 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <Kbd>Esc</Kbd>
          </div>

          <div ref={listRef} id={LIST_ID} role="listbox" aria-label={t('commandPalette.resultsAriaLabel')} className="min-h-0 flex-1 overflow-y-auto p-2">
            {results.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('commandPalette.noMatch', { query: query.trim() })}
              </p>
            )}
            {results.map((item, i) => {
              const Icon = item.icon
              const heading = grouped && (i === 0 || results[i - 1].section !== item.section)
              return (
                <Fragment key={item.id}>
                  {heading && <p className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{commandSectionLabel(item.section)}</p>}
                  <div
                    id={`cmd-${item.id}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseMove={() => i !== active && setActive(i)}
                    onClick={() => run(item)}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm',
                      i === active ? 'bg-primary/10 text-primary' : 'text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 opacity-80" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.group && <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">{item.group}</span>}
                  </div>
                </Fragment>
              )
            })}
          </div>

          <div className="flex items-center gap-4 border-t px-4 py-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> {t('commandPalette.select')}</span>
            <span className="flex items-center gap-1"><Kbd>Enter</Kbd> {t('commandPalette.open')}</span>
            <span className="ml-auto flex items-center gap-1"><Kbd>{MOD_KEY_LABEL}</Kbd><Kbd>K</Kbd> {t('commandPalette.toggle')}</span>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}
